import { Box, Button, makeStyles, TextField, Typography, useMediaQuery } from "@material-ui/core";
import { isAddress } from "ethers/lib/utils";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import DateGroupButton from "src/components/ScoringChart/tool-utils/DateGroupButton";
import CssSelector from "src/components/Selector/CssSelector";
import { useTransactionMonitoringSelector } from "src/context/monitoring/transactionMonitoringContext";
import { useParticularPoolsSelector } from "src/context/poolsContext";
import AddressTextField from "src/view/TransactionMonitoring/AddressTextField";
import { useWeb3Connect } from "src/wallet-connection/context/we3-connect-context";

const EVENT_TYPE_SELECT = [
  { value: null, label: "All Event" },
  { value: "deposit", label: "Deposit" },
  { value: "borrow", label: "Borrow" },
  { value: "withdraw", label: "Withdraw" },
  { value: "repay", label: "Repay" },
];

const useStyle = makeStyles((theme) => ({
  amount: {
    marginLeft: "1rem",
    width: "35%",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      margin: "0.5rem 0rem 0rem 0rem",
    },
  },
  confirmButton: {
    width: "100px",
    flexGrow: 1,
    [theme.breakpoints.down("xs")]: {
      marginTop: "1rem",
    },
  },
  rootInput: {
    "& .MuiInputBase-root": {
      height: "2.5rem",
    },
    flexGrow: 1,
    [theme.breakpoints.only("xs")]: {
      marginBottom: "0.5rem",
      width: "100%",
      marginRight: "0rem",
    },
    marginRight: "1rem",
  },
}));

const dateOption = () => {
  var currentTime = Math.floor(Date.now() / 1000);
  return [
    { label: "1D", from: currentTime - 86400, to: currentTime },
    { label: "1W", from: currentTime - 604800, to: currentTime },
    { label: "1M", from: currentTime - 2592000, to: currentTime },
    { label: "3M", from: currentTime - 7776000, to: currentTime },
  ];
};

export default function FilterTool({ ...props }) {
  const cls = useStyle();
  const { pool } = useParams();
  const { address } = useWeb3Connect();
  const match = useMediaQuery((theme) => theme.breakpoints.only("xs"));
  const { transactionData, fetchTransactionData } = useTransactionMonitoringSelector((state) => state);

  const [eventType, setEventType] = useState({ value: null, label: "All Event" });
  const [token, setToken] = useState(null);
  const [fromAmount, setFromAmount] = useState(null);
  const [toAmount, setToAmount] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(address ?? "");
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);

  const { token: tokenConfig, fetch } = useParticularPoolsSelector(pool);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    if (transactionData?.active === undefined) {
      var currentTime = Math.floor(Date.now() / 1000);
      fetchTransactionData(pool, { user: address, from: currentTime - 2592000, to: currentTime }, { active: 2 });
    }
  }, [transactionData?.active, pool]);

  const tokenSelect = useMemo(() => {
    if (tokenConfig) {
      const _token = Object.values(tokenConfig);
      const _temp = _token.map((element) => ({
        value: element.address,
        label: element.symbol,
      }));
      const _result = [{ value: null, label: "All Token" }];
      return _result.concat(_temp);
    }
    return null;
  }, [tokenConfig]);

  useEffect(() => {
    if (tokenSelect) setToken(tokenSelect[0]);
  }, [tokenSelect]);

  const onConfirmClick = () => {
    if (checkValidAmount() === false) return;
    else {
      fetchTransactionData(pool, {
        user: selectedAddress,
        event_type: eventType.value,
        token: token.value,
        from: from,
        to: to,
        min: fromAmount,
        max: toAmount,
      });
    }
  };

  const checkValidAmount = () => {
    if (parseFloat(fromAmount) < 0) return false;
    if (parseFloat(toAmount) < 0) return false;
    if (parseFloat(fromAmount) > parseFloat(toAmount)) return false;
    if (fromAmount === undefined || toAmount === undefined) return true;
    else return true;
  };

  useEffect(() => {
    checkValidAmount();
  }, [fromAmount, toAmount]);

  const handleFromAmount = (ev) => {
    setFromAmount(ev.target.value);
  };

  const handleToAmount = (ev) => {
    setToAmount(ev.target.value);
  };

  const checkValidAddress = useMemo(() => {
    if (selectedAddress.length > 0) return isAddress(selectedAddress);
    else return true;
  }, [selectedAddress]);

  const error = useMemo(() => {
    return checkValidAddress && checkValidAmount();
  }, [checkValidAddress, checkValidAmount()]);

  return (
    <Box {...props}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mt={2}>
        <CssSelector
          style={match ? { width: "100%", marginBottom: "0.5rem" } : {}}
          mr={match ? 0 : 1}
          defaultSelectedItem={eventType}
          config={EVENT_TYPE_SELECT}
          event={{
            onChooseItem: (_, item) => {
              setEventType(item);
            },
          }}
        />
        {tokenSelect && tokenConfig && (
          <CssSelector
            style={match ? { width: "100%", marginBottom: "0.5rem" } : {}}
            mr={match ? 0 : 1}
            defaultSelectedItem={token}
            config={tokenSelect}
            event={{
              onChooseItem: (_, item) => {
                setToken(item);
              },
            }}
          />
        )}
        <AddressTextField
          fullWidth={match}
          variant="outlined"
          placeholder="Parse your account address"
          address={selectedAddress}
          events={{ onChange: (value) => setSelectedAddress(value), onClear: () => setSelectedAddress("") }}
          className={cls.rootInput}
        />
        <DateGroupButton
          fullWidth={match}
          defaultActive={transactionData[pool]?.active ?? 0}
          picker={{
            continueClick: (from, to, _) => {
              setFrom(from);
              setTo(to);
            },
          }}
          config={dateOption().map((item, _) => ({
            data: item,
            onClick: (data, _) => {
              setFrom(data.from);
              setTo(data.to);
            },
          }))}
        />
      </Box>
      <Box
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        flexDirection={match ? "column" : "row"}
        mt={match ? 0 : 1}
      >
        <Typography>Amount (USD)</Typography>
        <TextField
          type="number"
          fullWidth={match}
          size="small"
          variant="outlined"
          placeholder="From Amount"
          className={cls.amount}
          onChange={handleFromAmount}
        />
        <TextField
          type="number"
          fullWidth={match}
          size="small"
          variant="outlined"
          placeholder="To Amount"
          className={cls.amount}
          onChange={handleToAmount}
        />
      </Box>
      <Button
        fullWidth={match}
        variant="contained"
        color="primary"
        onClick={() => onConfirmClick()}
        disabled={!error}
        className={cls.confirmButton}
      >
        Confirm
      </Button>
      {checkValidAmount() === false && <Typography style={{ color: "red" }}>Invalid amount!</Typography>}
    </Box>
  );
}
